import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaUser,
  FaRocket,
  FaBook,
  FaChartBar,
  FaComments,
  FaDollarSign,
  FaUsers,
  FaClipboardList,
  FaBell,
  FaCheck,
  FaTimes,
  FaInfinity,
  FaExclamationTriangle,
  FaClock,
  FaChartLine,
  FaMobileAlt,
  FaArrowRight
} from 'react-icons/fa';
import './Landing.css';

const SECTIONS_COUNT = 7; // Hero, Pain Points, Features, Use Cases, Pricing, FAQ, CTA

function Landing() {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const goToSection = useCallback((index) => {
    if (isAnimating || index === currentIndex || index < 0 || index >= SECTIONS_COUNT) return;
    setIsAnimating(true);
    setCurrentIndex(index);
    setTimeout(() => setIsAnimating(false), 1000); // 1초 슬라이드 애니메이션
  }, [currentIndex, isAnimating]);

  // Wheel event - h-bridge 스타일 (사례 섹션 스크롤 처리 추가)
  useEffect(() => {
    let wheelTimeout;
    const handleWheel = (e) => {
      // 마지막 섹션(index 6)에서 아래로 스크롤 시 푸터로 이동
      if (currentIndex === SECTIONS_COUNT - 1 && e.deltaY > 0) {
        e.preventDefault();
        clearTimeout(wheelTimeout);
        wheelTimeout = setTimeout(() => {
          const footer = document.querySelector('.landing-footer');
          if (footer) {
            footer.scrollIntoView({ behavior: 'smooth' });
          }
        }, 50);
        return;
      }

      // 사례 섹션(index 3)에서는 스크롤 영역 확인
      if (currentIndex === 3) {
        const scrollableElement = document.querySelector('.section-content-wrapper.scrollable');
        if (scrollableElement) {
          const { scrollTop, scrollHeight, clientHeight } = scrollableElement;
          const isAtTop = scrollTop === 0;
          const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 1;

          // 위로 스크롤 중이고 맨 위에 있을 때만 이전 섹션으로
          if (e.deltaY < 0 && !isAtTop) {
            return; // 섹션 내부 스크롤 허용
          }
          // 아래로 스크롤 중이고 맨 아래에 있을 때만 다음 섹션으로
          if (e.deltaY > 0 && !isAtBottom) {
            return; // 섹션 내부 스크롤 허용
          }
        }
      }

      e.preventDefault();
      clearTimeout(wheelTimeout);
      wheelTimeout = setTimeout(() => {
        const direction = e.deltaY > 0 ? 1 : -1;
        goToSection(currentIndex + direction);
      }, 50);
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => window.removeEventListener('wheel', handleWheel);
  }, [currentIndex, goToSection]);

  // Touch events
  useEffect(() => {
    let touchStartY = 0;
    const handleTouchStart = (e) => {
      touchStartY = e.touches[0].clientY;
    };
    const handleTouchEnd = (e) => {
      const diff = touchStartY - e.changedTouches[0].clientY;
      if (Math.abs(diff) > 50) {
        goToSection(currentIndex + (diff > 0 ? 1 : -1));
      }
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [currentIndex, goToSection]);

  // Keyboard events - Arrow keys and Page Up/Down
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        e.preventDefault();
        goToSection(currentIndex + 1);
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault();
        goToSection(currentIndex - 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, goToSection]);

  const getSectionClass = (index) => {
    let classes = 'section';
    if (index === currentIndex) {
      classes += ' active in-view';
    } else if (index < currentIndex) {
      classes += ' prev';
    }
    return classes;
  };

  return (
    <div className="fullpage-wrapper">
      <div className="sections-wrapper">
        {/* Section 0: Hero */}
        <section className={getSectionClass(0) + ' hero-section'}>
          {/* 배경 레이어 */}
          <div className="section-bg hero-bg"></div>

          {/* 컨텐츠 레이어 */}
          <div className="section-content-wrapper">
            <div className="hero-container">
              <h1 className="hero-title">학원 운영의 모든 것을 한 곳에서</h1>
              <p className="hero-subtitle">
                출결관리부터 수업일지, 학부모 알림까지
              </p>

              <div className="action-boxes">
                <div className="action-box" onClick={() => navigate('/login')}>
                  <FaUser className="box-icon" />
                  <h2>기존 회원</h2>
                  <p>이미 계정이 있으신가요?</p>
                  <button className="btn btn-primary">로그인하기</button>
                </div>

                <div className="action-box" onClick={() => navigate('/register')}>
                  <FaRocket className="box-icon" />
                  <h2>신규 가입</h2>
                  <p>지금 바로 시작해보세요!</p>
                  <button className="btn btn-success">무료로 시작하기</button>
                </div>
              </div>

              <div className="scroll-indicator">
                <span>아래로 스크롤</span>
              </div>
            </div>
          </div>
        </section>

        {/* Section 1: Pain Points */}
        <section className={getSectionClass(1) + ' pain-points-section'}>
          {/* 배경 레이어 */}
          <div className="section-bg pain-points-bg"></div>

          {/* 컨텐츠 레이어 */}
          <div className="section-content-wrapper">
            <div className="section-container">
              <h2 className="section-title animate-on-scroll">이런 고민 있으신가요?</h2>
              <p className="section-subtitle animate-on-scroll delay-1">학원 운영의 어려움, EduStaff가 이해합니다</p>

              <div className="pain-points-grid">
                <div className="pain-point-card animate-on-scroll delay-2">
                  <FaExclamationTriangle className="pain-icon" />
                  <h3>엑셀 관리의 한계</h3>
                  <p>학생 정보, 출결, 수강료를 엑셀로 관리하느라 매일 2-3시간씩 낭비하고 계신가요?</p>
                </div>

                <div className="pain-point-card animate-on-scroll delay-2">
                  <FaClock className="pain-icon" />
                  <h3>학부모 응대 시간</h3>
                  <p>출결 확인, 수업 진도 문의 전화가 너무 많아 정작 수업 준비할 시간이 없으신가요?</p>
                </div>

                <div className="pain-point-card animate-on-scroll delay-3">
                  <FaChartLine className="pain-icon" />
                  <h3>놓치는 수강료</h3>
                  <p>미납 학생을 일일이 확인하고 연락하느라 힘드시고, 때로는 놓치기도 하시나요?</p>
                </div>

                <div className="pain-point-card animate-on-scroll delay-3">
                  <FaMobileAlt className="pain-icon" />
                  <h3>수업일지 작성 부담</h3>
                  <p>매번 수업일지 쓰는 게 부담스러워 학부모 소통이 뜸해지고 계신가요?</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Features */}
        <section className={getSectionClass(2) + ' features-section'}>
          {/* 배경 레이어 */}
          <div className="section-bg features-bg"></div>

          {/* 컨텐츠 레이어 */}
          <div className="section-content-wrapper">
            <div className="section-container">
              <h2 className="section-title animate-on-scroll">EduStaff가 해결합니다</h2>
              <p className="section-subtitle animate-on-scroll delay-1">학원 운영을 더 쉽고 효율적으로</p>

              <div className="features-grid">
                <div className="feature-item animate-on-scroll delay-2">
                  <FaBook className="feature-icon" />
                  <h3>자동 관리</h3>
                  <p>학생, 반, 과목, 출결을 클릭 몇 번으로 관리</p>
                </div>

                <div className="feature-item animate-on-scroll delay-2">
                  <FaChartBar className="feature-icon" />
                  <h3>스마트 리포트</h3>
                  <p>수업일지를 모바일로 2분 만에 작성하고 자동 발송</p>
                </div>

                <div className="feature-item animate-on-scroll delay-2">
                  <FaComments className="feature-icon" />
                  <h3>실시간 알림</h3>
                  <p>출결, 공지를 자동으로 학부모에게 알림톡 발송</p>
                </div>

                <div className="feature-item animate-on-scroll delay-3">
                  <FaDollarSign className="feature-icon" />
                  <h3>수강료 자동화</h3>
                  <p>미납자 자동 확인, 알림 발송으로 수납률 UP</p>
                </div>

                <div className="feature-item animate-on-scroll delay-3">
                  <FaUsers className="feature-icon" />
                  <h3>직원 협업</h3>
                  <p>실시간 채팅과 모니터링으로 팀워크 강화</p>
                </div>

                <div className="feature-item animate-on-scroll delay-3">
                  <FaClipboardList className="feature-icon" />
                  <h3>통합 대시보드</h3>
                  <p>모든 정보를 한눈에, 언제 어디서나 확인</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3: Use Cases - 스크롤 가능 */}
        <section className={getSectionClass(3) + ' use-cases-section'}>
          {/* 배경 레이어 */}
          <div className="section-bg use-cases-bg"></div>

          {/* 컨텐츠 레이어 - 스크롤 가능 */}
          <div className="section-content-wrapper scrollable">
            <div className="section-container">
              <h2 className="section-title animate-on-scroll">실제 활용 사례</h2>
              <p className="section-subtitle animate-on-scroll delay-1">다양한 규모와 형태의 학원에서 사용 중입니다</p>

              <div className="use-cases-container">
                {/* Case 1: 소규모 - 왼쪽 정렬 */}
                <div className="use-case-card left-aligned animate-on-scroll delay-2">
                  <div className="case-header">
                    <div className="case-badge">📍 강남구 대치동</div>
                    <h3>수학학원 (학생 30명)</h3>
                  </div>
                  <div className="case-content">
                    <div className="case-problem">
                      <h4>문제</h4>
                      <ul>
                        <li>엑셀 관리에 매일 2시간</li>
                        <li>학부모 전화 응대에 지쳐</li>
                        <li>미납 학생 자주 놓침</li>
                      </ul>
                    </div>
                    <FaArrowRight className="arrow-icon" />
                    <div className="case-solution">
                      <h4>해결</h4>
                      <ul>
                        <li>자동 출결 + 알림톡</li>
                        <li>수강료 자동 계산</li>
                        <li>모바일로 언제나 확인</li>
                      </ul>
                    </div>
                  </div>
                  <div className="case-results">
                    <div className="result-item">
                      <span className="result-number">50%</span>
                      <span className="result-label">업무시간 절감</span>
                    </div>
                    <div className="result-item">
                      <span className="result-number">95%</span>
                      <span className="result-label">수강료 납부율</span>
                    </div>
                  </div>
                </div>

                {/* Case 2: 1인 과외 - 오른쪽 정렬 */}
                <div className="use-case-card right-aligned animate-on-scroll delay-3">
                  <div className="case-header">
                    <h3>영어 과외 (학생 15명)</h3>
                    <div className="case-badge">📍 대구 수성구</div>
                  </div>
                  <div className="case-content">
                    <div className="case-solution">
                      <h4>해결</h4>
                      <ul>
                        <li>2분 만에 수업일지 작성</li>
                        <li>학생별 진도 자동 기록</li>
                        <li>학부모 자동 발송</li>
                      </ul>
                    </div>
                    <FaArrowRight className="arrow-icon reversed" />
                    <div className="case-problem">
                      <h4>문제</h4>
                      <ul>
                        <li>학생별 진도 관리 복잡</li>
                        <li>수업일지 쓸 시간 없음</li>
                        <li>피드백 전달 번거로움</li>
                      </ul>
                    </div>
                  </div>
                  <div className="case-results">
                    <div className="result-item">
                      <span className="result-number">80%</span>
                      <span className="result-label">관리시간 단축</span>
                    </div>
                    <div className="result-item">
                      <span className="result-number">실시간</span>
                      <span className="result-label">학부모 소통</span>
                    </div>
                  </div>
                </div>

                {/* Case 3: 대형 - 중앙, 강조 */}
                <div className="use-case-card center-featured animate-on-scroll delay-4">
                  <div className="case-header">
                    <div className="case-badge featured">📍 서울 용산구</div>
                    <h3>종합학원 (학생 200명, 직원 10명)</h3>
                  </div>
                  <div className="case-content horizontal">
                    <div className="case-problem">
                      <h4>문제</h4>
                      <ul>
                        <li>직원 10명 업무 파악 어려움</li>
                        <li>부서 간 소통 비효율</li>
                        <li>학생 관리 일관성 부족</li>
                      </ul>
                    </div>
                    <FaArrowRight className="arrow-icon large" />
                    <div className="case-solution">
                      <h4>해결</h4>
                      <ul>
                        <li>실시간 직원 모니터링</li>
                        <li>내부 채팅으로 즉시 소통</li>
                        <li>통합 대시보드 관리</li>
                      </ul>
                    </div>
                  </div>
                  <div className="case-results large">
                    <div className="result-item">
                      <span className="result-number">30%</span>
                      <span className="result-label">업무 효율 향상</span>
                    </div>
                    <div className="result-item">
                      <span className="result-number">60%</span>
                      <span className="result-label">소통시간 단축</span>
                    </div>
                    <div className="result-item">
                      <span className="result-number">대폭</span>
                      <span className="result-label">일관성 개선</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: Pricing */}
        <section className={getSectionClass(4) + ' pricing-section'}>
          {/* 배경 레이어 */}
          <div className="section-bg pricing-bg"></div>

          {/* 컨텐츠 레이어 */}
          <div className="section-content-wrapper">
            <div className="section-container">
              <h2 className="section-title animate-on-scroll">요금제</h2>
              <p className="section-subtitle animate-on-scroll delay-1">학원 규모와 필요에 맞는 플랜을 선택하세요</p>

              <div className="pricing-grid">
                <div className="pricing-card animate-on-scroll delay-2">
                  <h3 className="plan-name">무료</h3>
                  <div className="plan-price">
                    <span className="amount">0</span>
                    <span className="currency">원</span>
                  </div>
                  <ul className="plan-features">
                    <li><FaCheck className="check-icon" /> 학생 최대 50명</li>
                    <li><FaCheck className="check-icon" /> 저장공간 100MB</li>
                    <li><FaCheck className="check-icon" /> 기본 관리 기능</li>
                    <li><FaTimes className="times-icon" /> 리포트</li>
                    <li><FaTimes className="times-icon" /> 직원 관리</li>
                  </ul>
                  <div className="plan-note">
                    알림을 보내지 않으면<br />완전히 무료!
                  </div>
                  <button className="btn btn-outline" onClick={() => navigate('/register')}>
                    시작하기
                  </button>
                </div>

                <div className="pricing-card recommended animate-on-scroll delay-3">
                  <div className="badge">추천</div>
                  <h3 className="plan-name">Basic</h3>
                  <div className="plan-price">
                    <span className="amount">7,900</span>
                    <span className="currency">원</span>
                    <span className="period">/월</span>
                  </div>
                  <ul className="plan-features">
                    <li><FaInfinity className="infinity-icon" /> 학생 무제한</li>
                    <li><FaCheck className="check-icon" /> 저장공간 500MB</li>
                    <li><FaCheck className="check-icon" /> 매월 10,000P 지급</li>
                    <li><FaCheck className="check-icon" /> 리포트 기능</li>
                    <li><FaCheck className="check-icon" /> 직원 관리</li>
                  </ul>
                  <div className="plan-note">
                    예측 가능한 월 비용<br />포인트로 편리하게
                  </div>
                  <button className="btn btn-primary" onClick={() => navigate('/register')}>
                    시작하기
                  </button>
                </div>

                <div className="pricing-card animate-on-scroll delay-4">
                  <h3 className="plan-name">Pro</h3>
                  <div className="plan-price">
                    <span className="amount">15,900</span>
                    <span className="currency">원</span>
                    <span className="period">/월</span>
                  </div>
                  <ul className="plan-features">
                    <li><FaInfinity className="infinity-icon" /> 학생 무제한</li>
                    <li><FaCheck className="check-icon" /> 저장공간 2GB</li>
                    <li><FaBell className="bell-icon" /> 푸시 무제한 무료</li>
                    <li><FaComments className="chat-icon" /> 알림톡 700건/월</li>
                    <li><FaCheck className="check-icon" /> 모든 기능 포함</li>
                  </ul>
                  <div className="plan-note">
                    대형 학원 최적<br />무제한 알림
                  </div>
                  <button className="btn btn-primary" onClick={() => navigate('/register')}>
                    시작하기
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 5: FAQ */}
        <section className={getSectionClass(5) + ' faq-section'}>
          {/* 배경 레이어 */}
          <div className="section-bg faq-bg"></div>

          {/* 컨텐츠 레이어 */}
          <div className="section-content-wrapper">
            <div className="section-container">
              <h2 className="section-title animate-on-scroll">자주 묻는 질문</h2>

              <div className="faq-grid">
                <div className="faq-item animate-on-scroll delay-1">
                  <h3>무료 플랜으로 얼마나 사용할 수 있나요?</h3>
                  <p>학생 50명까지, 100MB 저장공간으로 모든 기본 기능을 사용할 수 있습니다. 알림을 보내지 않는다면 완전히 무료입니다.</p>
                </div>

                <div className="faq-item animate-on-scroll delay-2">
                  <h3>Basic 플랜의 포인트는 어떻게 사용하나요?</h3>
                  <p>매월 10,000P가 자동 지급됩니다. 푸시 10P, 알림톡 30P가 차감되며, 포인트는 다음 달로 이월되지 않습니다.</p>
                </div>

                <div className="faq-item animate-on-scroll delay-3">
                  <h3>Pro 플랜의 알림톡은 얼마나 제공되나요?</h3>
                  <p>매월 700건까지 무료이며, 초과분은 건당 30원이 청구됩니다. 푸시 알림은 무제한 무료입니다.</p>
                </div>

                <div className="faq-item animate-on-scroll delay-4">
                  <h3>플랜 변경은 언제든지 가능한가요?</h3>
                  <p>네, 언제든지 플랜을 업그레이드하거나 다운그레이드할 수 있습니다.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 6: Final CTA */}
        <section className={getSectionClass(6) + ' cta-section'}>
          {/* 배경 레이어 */}
          <div className="section-bg cta-bg"></div>

          {/* 컨텐츠 레이어 */}
          <div className="section-content-wrapper">
            <div className="cta-container">
              <h2 className="animate-on-scroll">지금 바로 시작하세요</h2>
              <p className="animate-on-scroll delay-1">신용카드 등록 없이, 학생 50명까지 완전 무료</p>
              <button className="btn btn-large btn-success animate-on-scroll delay-2" onClick={() => navigate('/register')}>
                무료로 시작하기
              </button>
            </div>
          </div>
        </section>

      </div>

      {/* Footer - Outside sections wrapper */}
      <footer className="landing-footer">
        <div className="footer-content">
          {/* 회사정보 */}
          <div className="footer-section">
            <h4 className="footer-section-title">회사정보</h4>
            <div className="footer-brand">
              <h3>EduStaff</h3>
              <p>학원 운영의 모든 것</p>
            </div>
            <div className="footer-company-info">
              <p>상호: 아이지에듀랩</p>
              <p>대표자: 송인규</p>
              <p>사업자등록번호: 497-30-01710</p>
              <p>주소: 서울특별시 강동구 양재대로 1650</p>
            </div>
          </div>

          {/* 서비스 */}
          <div className="footer-section">
            <h4 className="footer-section-title">서비스</h4>
            <div className="footer-links-column">
              <button className="footer-link-btn" onClick={() => goToSection(2)}>주요 기능</button>
              <button className="footer-link-btn" onClick={() => goToSection(4)}>요금제</button>
              <button className="footer-link-btn" onClick={() => goToSection(3)}>실제 사례</button>
              <button className="footer-link-btn" onClick={() => goToSection(5)}>자주 묻는 질문</button>
            </div>
          </div>

          {/* 고객지원 */}
          <div className="footer-section">
            <h4 className="footer-section-title">고객지원</h4>
            <div className="footer-links-column">
              <div className="footer-support-info">
                <p>이메일: acaeduno1@gmail.com</p>
                <p>평일 09:00 - 18:00</p>
                <p>주말 및 공휴일 휴무</p>
                <a href="http://pf.kakao.com/_MdUxgn" target="_blank" rel="noopener noreferrer" className="kakao-link">
                  💬 카카오톡 상담하기
                </a>
              </div>
              <div className="footer-legal-links">
                <a href="/terms" className="footer-link">이용약관</a>
                <a href="/privacy" className="footer-link">개인정보처리방침</a>
              </div>
              <button className="btn btn-success btn-footer" onClick={() => navigate('/register')}>
                무료로 시작하기
              </button>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2024 EduStaff. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
